import React, {useLayoutEffect, useMemo, useRef, useState} from "react";
import {useNavigate} from "react-router-dom";
import {ChevronDownIcon, ChevronRightIcon} from "../selector/SelectorIcons";
import {formatKvListInline, formatKvListSummary, KV_LIST_INLINE_MAX} from "./detailKvListLabels";
import {hasKvListNavigation, navigateKvListItem} from "./detailKvListNavigation";
import linkIcon from "../../resources/icon/link.png";

function defaultGetLabel(item) {
    return item?.assetName ?? item?.name ?? item?.title ?? "";
}

function KvListItemLink({item, listType, navigate, className = "detail-kv-expandable-list__inline-link"}) {
    if (!hasKvListNavigation(listType) || item?.id == null) {
        return null;
    }

    return (
        <button
            type="button"
            className={className}
            title="바로가기"
            aria-label="바로가기"
            onClick={(event) => {
                event.preventDefault();
                event.stopPropagation();
                navigateKvListItem(navigate, item, listType);
            }}
        >
            <img src={linkIcon} alt="" className="detail-kv-expandable-list__link-icon print-hidden" />
        </button>
    );
}

/**
 * KV 셀 — 다건 목록
 * - 3개 이하·공간 충분: 쉼표 구분 인라인
 * - 4개 이상 또는 공간 부족: 요약 + 펼치기
 */
export function DetailKvExpandableList({
    items,
    getLabel = defaultGetLabel,
    listType,
    inlineMax = KV_LIST_INLINE_MAX,
}) {
    const navigate = useNavigate();
    const [expanded, setExpanded] = useState(false);
    const [useCompact, setUseCompact] = useState(false);
    const inlineMeasureRef = useRef(null);
    const showItemLink = hasKvListNavigation(listType);

    const entries = useMemo(
        () => (items || [])
            .map((item) => ({item, label: getLabel(item)}))
            .filter(({label}) => Boolean(label)),
        [items, getLabel],
    );
    const labels = useMemo(() => entries.map((entry) => entry.label), [entries]);
    const inlineText = useMemo(() => formatKvListInline(labels), [labels]);
    const shouldTryInline = labels.length > 1 && labels.length <= inlineMax;

    useLayoutEffect(() => {
        if (!shouldTryInline) {
            setUseCompact(true);
            return;
        }

        const measure = () => {
            const el = inlineMeasureRef.current;
            if (!el) return;
            setUseCompact(el.scrollWidth > el.clientWidth + 1);
        };

        measure();

        const el = inlineMeasureRef.current;
        if (!el || typeof ResizeObserver === "undefined") return undefined;

        const observer = new ResizeObserver(measure);
        observer.observe(el);
        return () => observer.disconnect();
    }, [shouldTryInline, inlineText, labels.length]);

    if (labels.length === 0) {
        return <span className="asset-detail-kv-value asset-detail-kv-empty">&nbsp;</span>;
    }

    if (labels.length === 1) {
        const {item, label} = entries[0];
        return (
            <span className="asset-detail-kv-value detail-kv-expandable-list__inline-text detail-kv-expandable-list__inline-text--single">
                <span className="detail-kv-expandable-list__inline-item">
                    <span className="detail-kv-expandable-list__inline-item-label">{label}</span>
                    {showItemLink && (
                        <KvListItemLink item={item} listType={listType} navigate={navigate} />
                    )}
                </span>
            </span>
        );
    }

    if (shouldTryInline && !useCompact) {
        return (
            <span
                ref={inlineMeasureRef}
                className="asset-detail-kv-value detail-kv-expandable-list__inline-text"
            >
                {entries.map(({item, label}, index) => (
                    <React.Fragment key={`${label}-${item?.id ?? index}`}>
                        {index > 0 && (
                            <span className="detail-kv-expandable-list__inline-sep">, </span>
                        )}
                        <span className="detail-kv-expandable-list__inline-item">
                            <span className="detail-kv-expandable-list__inline-item-label">{label}</span>
                            {showItemLink && (
                                <KvListItemLink item={item} listType={listType} navigate={navigate} />
                            )}
                        </span>
                    </React.Fragment>
                ))}
            </span>
        );
    }

    const summaryText = formatKvListSummary(labels[0], labels.length - 1, listType);

    return (
        <div className="detail-kv-expandable-list">
            <div className="detail-kv-expandable-list__row">
                <span className="detail-kv-expandable-list__inline">
                    <span className="asset-detail-kv-value detail-kv-expandable-list__summary">
                        {summaryText}
                    </span>
                    <button
                        type="button"
                        className="detail-kv-expandable-list__toggle"
                        title={expanded ? "접기" : "펼치기"}
                        aria-label={expanded ? "접기" : "펼치기"}
                        aria-expanded={expanded}
                        onClick={() => setExpanded((prev) => !prev)}
                    >
                        {expanded ? (
                            <ChevronDownIcon />
                        ) : (
                            <ChevronRightIcon />
                        )}
                    </button>
                </span>
            </div>
            {expanded && (
                <ul className="detail-kv-expandable-list__items">
                    {entries.map(({item, label}, index) => (
                        <li key={`${label}-${item?.id ?? index}`} className="detail-kv-expandable-list__item">
                            <span className="detail-kv-expandable-list__item-label">{label}</span>
                            {showItemLink && item?.id != null && (
                                <button
                                    type="button"
                                    className="user-selection-chips-view-all detail-kv-expandable-list__item-link"
                                    onClick={() => navigateKvListItem(navigate, item, listType)}
                                >
                                    바로가기
                                </button>
                            )}
                        </li>
                    ))}
                </ul>
            )}
        </div>
    );
}
